import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Select,
  Tooltip
} from "antd";
import FormItem from "antd/es/form/FormItem";
import TextArea from "antd/lib/input/TextArea";
import { ResponsibleContractorDropdown } from "components/cell-editor/drop-down-editor";
import SelectFeatureSpecSections from "components/feature-spec-section-dropdown/feature-spec-sections";
import GCReviewersDropdown from "components/gc-reveiewer-dropdown";
import DependedCompanyUserDropdownComponent from "components/responsible-designers";
import CreateSpecSectionModal from "components/submittal-details/create-spec-section";
import { LocationSearchNotFoundMessages } from "components/widgets/location-search-not-found-messages";
import { ErrorMessages, SidepanelStyles } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import { MUTATION_CREATE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { useShippingLocation } from "hooks/material";
import { useProjectParticipants } from "hooks/project-participants";
import useProjectTemplateListHook from "pages/preconstruction-details/components/workflow-template-dropdown/use-project-template-list";
import { useContext, useEffect, useRef, useState } from "react";
import { validatorForFieldValue } from "utils/utils";

function CreateMaterialEntityComponent(props: {
  setDrawerOpen: Function;
  showDrawerOpen: boolean;
  modelTitle: string;
  onCreated: any;
}) {
  const [form] = Form.useForm();
  const { setDrawerOpen, showDrawerOpen, modelTitle, onCreated } = props;
  const [isLoading, setLoading] = useState(false);
  const {
    gqlClientForProject,
    tokenRetrievalState,
    columnHeaders: { materialHeaderMap }
  } = useContext(ProjectContext);

  const { projectParticipants } = useProjectParticipants();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showCreateSpecSectionModal, setShowCreateSpecSectionModal] =
    useState(false);
  const createSpecRef = useRef<any>(null);
  const token = tokenRetrievalState?.token;
  const tradePartnerID = Form.useWatch("responsible_contractor", form);

  const {
    options: MDBTemplatesDropdownOptions,
    defaultTemplateId,
    loading: TemplateLoading
  } = useProjectTemplateListHook({ featureId: FeatureTypes.MATERIALS });

  useEffect(() => {
    if (form && defaultTemplateId)
      form.setFieldValue("workflow_template_id", defaultTemplateId);
  }, [defaultTemplateId, form]);

  const [createEntityMutation] = useMutation<any>(
    gql(MUTATION_CREATE_FEATURE_INSTANCE),
    {
      client: gqlClientForProject
    }
  );

  const onFinish = async (values: any) => {
    const newUser = { ...values } as any;

    newUser.assignee_unregistered = null;
    if (values.assignee) {
      const isPoc =
        projectParticipants.allProjectUsersMap[values.assignee]?.type === "poc";
      if (isPoc) {
        newUser.assignee_unregistered = values.assignee;
        newUser.assignee = null;
      }
    }

    const variables = {
      feature_type: "MATERIALS",
      feature_instances: [
        {
          ...newUser,
          spec_sections: selectedIds
        }
      ]
    };

    setLoading(true);

    const creatResponse: any = await createEntityMutation({
      variables
    });
    setLoading(false);

    if (creatResponse?.data?.insert_feature_instances_multi?.ids?.length) {
      setSelectedIds([]);
      onCreated(
        values?.title,
        creatResponse?.data?.insert_feature_instances_multi?.ids[0]
      );
    } else {
      message.error("Unable to create Material.");
    }
  };

  const {
    searchLocation,
    fetching,
    selectOptions: shippingLocationOptionResults,
    searchedString
  } = useShippingLocation({
    gqlClientForProject,
    projectToken: tokenRetrievalState?.token
  });

  return (
    <Modal
      className="custom-drawer with-body-scroll"
      title={modelTitle}
      width={420}
      style={SidepanelStyles.style}
      data-testid="index-modal-material-log"
      footer={
        <div className="flex justify-end space-x-2 items-center">
          <Button
            form="CreateMaterialForm"
            disabled={isLoading || showCreateSpecSectionModal}
            onClick={() => {
              setSelectedIds([]);
              setDrawerOpen(false);
            }}
            data-testid="index-button-cancel"
          >
            Cancel
          </Button>
          <Button
            form="CreateMaterialForm"
            loading={isLoading}
            disabled={isLoading || showCreateSpecSectionModal}
            htmlType="submit"
            type="primary"
            data-testid="index-button-create-material"
          >
            Create Material
          </Button>
        </div>
      }
      open={showDrawerOpen}
      onCancel={() => {
        setSelectedIds([]);
        setDrawerOpen(false);
      }}
      destroyOnClose
    >
      <div className="body-content-wrapper">
        <Form
          preserve={false}
          form={form}
          id="CreateMaterialForm"
          scrollToFirstError
          layout="vertical"
          onFinish={onFinish}
          disabled={showCreateSpecSectionModal}
          data-testid="index-form-create-material"
        >
          <FormItem
            name="title"
            label={materialHeaderMap?.name}
            rules={[
              {
                required: true,
                validateTrigger: "onSubmit",
                message: ErrorMessages.materialTitle
              },
              {
                validator: (_, value) => {
                  return validatorForFieldValue(
                    "title",
                    value?.trim(),
                    "MATERIALS",
                    token,
                    ErrorMessages.MaterialTitleMsg
                  );
                }
              }
            ]}
            data-testid="index-formitem-title"
          >
            <Input
              placeholder="Enter the material title"
              data-testid="index-input-title"
            />
          </FormItem>
          <FormItem
            label={materialHeaderMap?.spec_section_no}
            data-testid="index-formitem-spec-section"
          >
            <SelectFeatureSpecSections
              mode="single"
              placeholder="Search Spec Section"
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              onCreateSpecSectionClick={(showSpecSectionView: boolean) => {
                setShowCreateSpecSectionModal(showSpecSectionView);
                setTimeout(() => {
                  if (createSpecRef.current)
                    createSpecRef.current.scrollIntoView({
                      behavior: "smooth",
                      block: "end"
                    });
                }, 100);
              }}
              data-testid="index-select-spec-section"
            />
          </FormItem>
          <FormItem
            name="tag_name"
            label={materialHeaderMap?.tag_name}
            data-testid="index-formitem-tag-name"
          >
            <Input
              placeholder="Enter Material Tag"
              data-testid="index-input-tag-name"
            />
          </FormItem>
          <FormItem
            name="description"
            label={materialHeaderMap?.description}
            data-testid="index-formitem-description"
          >
            <TextArea
              placeholder="Enter material description"
              data-testid="index-input-description"
            />
          </FormItem>

          <Form.Item
            name="workflow_template_id"
            label={materialHeaderMap?.date_block_template_name}
            data-testid="index-formitem-template-date-block"
          >
            <Select
              optionFilterProp="label"
              className="constructionSelect"
              options={MDBTemplatesDropdownOptions}
              loading={TemplateLoading}
              data-testid="index-select-template-date-block"
            />
          </Form.Item>

          <Form.Item
            name="place_id"
            label={materialHeaderMap?.shipping_location}
            data-testid="index-formitem-shipping-location"
          >
            <Select
              placeholder="Search by Address, City or State"
              showSearch
              allowClear
              filterOption={false}
              loading={fetching}
              options={shippingLocationOptionResults}
              onSearch={searchLocation}
              notFoundContent={
                <LocationSearchNotFoundMessages
                  searchedString={searchedString}
                  fetching={fetching}
                />
              }
              data-testid="index-select-shipping-location"
            />
          </Form.Item>

          <Form.Item
            name="manufacturer"
            label={materialHeaderMap?.manufacturer}
            data-testid="index-formitem-manufacturer"
          >
            <Input
              placeholder="Enter Manufacturer"
              data-testid="index-input-manufacturer"
            />
          </Form.Item>

          <Form.Item
            name="responsible_contractor"
            label={materialHeaderMap?.trade_partner}
            data-testid="index-formitem-responsible-contractor"
          >
            <ResponsibleContractorDropdown
              onChange={() => {
                form.setFieldValue("assignee", null);
              }}
              data-testid="index-dropdown-responsible-contractor"
            />
          </Form.Item>
          <Tooltip
            title={
              !tradePartnerID ? "First select a Responsible Contractor" : ""
            }
            placement="rightBottom"
            data-testid="index-tooltip-assignee"
          >
            <Form.Item
              name="assignee"
              label={materialHeaderMap?.assignee}
              data-testid="index-formitem-assignee"
            >
              <DependedCompanyUserDropdownComponent
                projectParticipantType="submitterUsers"
                selectedCompanyFieldId={tradePartnerID}
                disabled={!tradePartnerID}
                data-testid="index-dropdown-assignee"
              />
            </Form.Item>
          </Tooltip>

          <Form.Item
            label="GC Representative"
            name="gc_representative"
            data-testid="index-formitem-gc-representative"
          >
            <GCReviewersDropdown data-testid="index-dropdown-gc-representative" />
          </Form.Item>
        </Form>
        {showCreateSpecSectionModal && (
          <div
            ref={createSpecRef}
            className="pb-3"
            data-testid="index-create-spec-section-modal"
          >
            <Divider style={{ margin: "15px 0" }} />
            <div>
              <CreateSpecSectionModal
                isModalOpen={showCreateSpecSectionModal}
                onDoneCb={(newSpecSectionId: string) => {
                  if (newSpecSectionId) {
                    // setSelectedIds([...selectedIds, newSpecSectionId]);
                    setSelectedIds([newSpecSectionId]);
                  }
                  setShowCreateSpecSectionModal(false);
                }}
                data-testid="index-modal-create-spec-section"
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default CreateMaterialEntityComponent;
